import { useState, useEffect, useRef } from 'react';
import { Link } from '@inertiajs/react';
import { processDataTransferItemList } from '../Utils/dicom.js';
import axios from 'axios';
import { PlayIcon, CheckIcon, CubeIcon, XCircleIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import { saveInstance, deleteInstance } from '../Utils/indexedDB.js';
import Instance from '../Utils/instance.js';
import clsx from 'clsx';
import { Table, TableControlsButton, DateDisplay } from '../Components/table.jsx';
import { router } from '@inertiajs/react';
import { useUploadProgress } from '../Scripts/UploadProgressContext';

import { setup, renderDicomImage, renderDicomSegmentation } from '../Utils/cornerstone/display.js';

// debug only
window.axios = axios;

export default function UploadPage({ model, tasks }) {
    const [uploadProgress, setUploadProgress] = useState(0);
    const [instances, setInstances] = useState({});
    const [localInstanceSIUIDs, setLocalInstanceSIUIDs] = useState([]);
    const [showProcessingConfirmationDialog, setShowProcessingConfirmationDialog] = useState(false);
    const [storeProgress, setStoreProgress] = useState({});
    const [showFilePreviewInstance, setShowFilePreviewInstance] = useState(null);
    const [showOnlyHighlighted, setShowOnlyHighlighted] = useState(false);

    const query = new URLSearchParams(window.location.search);  // Access query parameters
    const highlightSID = query.get('SeriesInstanceUID');        // Get the specific ID from query parameters

    useEffect(() => {
        // Optionally, you can do something when highlightId changes
        console.warn(`Highlight SeriesInstanceUID from query: ${highlightSID}`);
    }, [highlightSID]);

    useEffect(() => {
        loadLocallySavedInstances();
    }, []);

    const isInstanceHighlighted = (instance) => {
        if (!highlightSID) return false; // No highlight ID provided
        return [instance.SeriesInstanceUID, instance.ReferencedSeriesInstanceUID].includes(highlightSID);
    };

    const renderInstance = async (instance) => {

        let imgFiles = null;
        let segFiles = null; 

        // instance must be local
        // if (!instance.local) {
        //     console.warn('Instance is not stored locally, cannot load DICOM files.');
        //     return;
        // }

        // handle modalities
        if (['MR', 'CT'].includes(instance.Modality)) {
        
            // load image files
            imgFiles = await Instance.getDicomFiles(instance);
        
        } else if (instance.Modality === 'SEG') {

            // load segmentation files
            segFiles = await Instance.getDicomFiles(instance);

            // find matching CT or MR instance
            const matchingInstance = Object.values(instances).find(
                (i) => i.SeriesInstanceUID === instance.ReferencedSeriesInstanceUID
            );

            if (!matchingInstance) {
                console.warn('No matching CT or MR instance found for SEG instance:', instance);
                return;
            }

            // load image files from matching instance
            imgFiles = await Instance.getDicomFiles(matchingInstance);
        }

        // render DICOM image
        if (imgFiles && imgFiles.length > 0) {
            await renderDicomImage(imgFiles);
        } else {
            console.warn('No image files found for instance:', instance.SeriesInstanceUID);
            return;
        }

        // render segmentation if available
        if (segFiles && segFiles.length == 1) {
            await renderDicomSegmentation(segFiles[0]);
        }
    }

    const selectedInstances = Object.values(instances)
        .filter((i) => i.selected);

    const updateInstance = (instance, saveInIndexDB = false) => {
        
        // Update instance in state
        setInstances((prevInstances) => {
            const updatedInstances = { ...prevInstances };
            updatedInstances[instance.SeriesInstanceUID] = instance;
            return updatedInstances;
        });

        // Save update in indexDB
        if(saveInIndexDB) {
            saveInstance(instance); 
        }
    };

    const removeLocallySavedInstance = async (seriesInstanceUID) => {
        // remove files from local bucket
        const root = await navigator.storage.getDirectory();
        const fileHandle = await root.getFileHandle(seriesInstanceUID + '.zip');
        await fileHandle.remove();
        
        // remove instance from IndexedDB
        await deleteInstance(seriesInstanceUID);

        // remove instance from localInstanceSIUIDs
        setLocalInstanceSIUIDs((prev) => prev.filter((siuid) => siuid !== seriesInstanceUID));

        // delete instance from state
        setInstances((prevInstances) => {
            const updatedInstances = { ...prevInstances };
            delete updatedInstances[seriesInstanceUID];
            return updatedInstances;
        });
    };

    const clearTable = () => {
        // note: only clears the visible instances, instance properties like tasks, local availablity, etc. are not cleared
        // the idea is to provide an empty canvas for the user to have a clean upload without changing anything really
        setInstances({});
        setUploadProgress(0);
    };

    const saveSelectedInstancesLocally = async () => {
        for (const instance of selectedInstances) {
            await Instance.store(instance, (progress) => {
                setStoreProgress((prev) => ({
                    ...prev,
                    [instance.SeriesInstanceUID]: progress,
                }));
            }, (instanceObj) => {
                updateInstance(instanceObj, false);
            });
        }
    }

    const loadLocallySavedInstances = () => {
        Instance.listLocalFiles((instance) => {
            instance.tasks = tasks.filter((task) => task.SeriesInstanceUID === instance.SeriesInstanceUID);
            instance.disabled = model && !model.modalities.includes(instance.Modality.toLowerCase());
            setInstances((prev) => ({
                ...prev,
                [instance.SeriesInstanceUID]: { ...instance },
            }));
            setLocalInstanceSIUIDs((prev) => [...prev, instance.SeriesInstanceUID]);
        });
    };

    const removeSelectedLocallySavedInstances = () => {
        const selectedSIUIDs = selectedInstances.map((i) => i.SeriesInstanceUID);
        selectedSIUIDs.forEach(async (siuid) => {
            await removeLocallySavedInstance(siuid);
        }); 
    }

    const removeAllLocallySavedInstances = async () => {

        // remove instances from local bucket and IndexedDB
        await Instance.deleteAll();

        // clear state
        setLocalInstanceSIUIDs([]);

        // remove all instances that have no Files
        setInstances((prevInstances) => {
            const updatedInstances = { ...prevInstances };
            Object.keys(updatedInstances).forEach((key) => {

                // mark instance as not local
                updatedInstances[key].local = false;

                // delete if Files is empty or null
                if (!updatedInstances[key].Files || updatedInstances[key].Files.length === 0) {
                    delete updatedInstances[key];
                }
            });
            return updatedInstances;
        });
    };

    // --- Add importDate to every new instance ---
    const handleProcessingProgress = (file) => {
        const SeriesInstanceUID = file.dicomData.SeriesInstanceUID;

        // check if the file is already in the the instance
        if ((SeriesInstanceUID in instances) && instances[SeriesInstanceUID].Files && instances[SeriesInstanceUID].Files.find(f => f.fileName === file.fileName)) {
            // console.warn(`File ${file.fileName} already exists in instance ${SeriesInstanceUID}. Skipping.`);
            return;
        }

        setInstances((prevInstances) => {
            const updatedInstances = { ...prevInstances };

            // create a new instance if it does not exist
            if (!updatedInstances[SeriesInstanceUID]) {
                updatedInstances[SeriesInstanceUID] = {
                    selected: true,
                    PatientID: file.dicomData.PatientID,
                    PatientName: file.dicomData.PatientName,
                    Modality: file.dicomData.Modality,
                    StudyDescription: file.dicomData.StudyDescription,
                    StudyInstanceUID: file.dicomData.StudyInstanceUID,
                    SeriesInstanceUID: file.dicomData.SeriesInstanceUID,
                    SOPInstanceUID: file.dicomData.SOPInstanceUID,
                    ReferencedSeriesInstanceUID: file.dicomData.ReferencedSeriesInstanceUID,
                    Files: [],
                    local: localInstanceSIUIDs.includes(SeriesInstanceUID),
                    size: 0,
                    importDate: new Date().toISOString(), // Set import date
                };
            }

            // Add file to the instance
            updatedInstances[SeriesInstanceUID] = {
                ...updatedInstances[SeriesInstanceUID],
                Files: [...(updatedInstances[SeriesInstanceUID].Files || []), file],
                size: (updatedInstances[SeriesInstanceUID].size || 0) + (file.fileData?.byteLength || 0),
                // Only set importDate if not already set
                importDate: updatedInstances[SeriesInstanceUID].importDate || new Date().toISOString(),
            };

            return updatedInstances;
        });
    };

    const handleStoreFile = async (instanceObj) => {
        setStoreProgress((prev) => ({
            ...prev,
            [instanceObj.SeriesInstanceUID]: 0,
        }));

        await Instance.store(instanceObj, (progress) => {
            setStoreProgress((prev) => ({
                ...prev,
                [instanceObj.SeriesInstanceUID]: progress,
            }));
        });

        setStoreProgress((prev) => {
            const updated = { ...prev };
            delete updated[instanceObj.SeriesInstanceUID];
            return updated;
        });

        updateInstance({
            ...instanceObj,
            local: true,
        }, false);
    };

    const handleDownloadFile = async (seriesInstanceUID) => {
        try {
            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(seriesInstanceUID + '.zip');
            const file = await fileHandle.getFile();
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = seriesInstanceUID + '.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };
    
    const TaskLink = (numTasks, instance) => (
        numTasks > 0 ? (
            <Link href={`/tasks?SeriesInstanceUID=${instance.SeriesInstanceUID}`} onClick={(e) => e.stopPropagation()} className="text-blue-500 hover:underline">
                <span className="w-7 inline-block text-right pr-1">
                    {numTasks}
                </span>
                Task{numTasks === 1 ? '' : 's'}
            </Link>
        ) : (
            <span className="text-gray-500">
                <span className="w-7 inline-block text-right pr-1">
                    No
                </span>
                Tasks
            </span>
        )
    )

    const InstanceActions = (_, instance) => (
        <div className="flex flex-row items-center gap-2">
            {instance.local ? (<>
                <button
                    className="text-red-500 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        removeLocallySavedInstance(instance.SeriesInstanceUID)
                    }}
                >
                    Remove
                </button>
                <button
                    className="text-blue-500 hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadFile(instance.SeriesInstanceUID)
                    }}
                >
                    Download
                </button>
            </>) : (
                <div className="flex flex-col items-start">
                    <button
                        className={`text-blue-500 hover:underline ${
                            !instance.Files?.length
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStoreFile(instance)
                        }}
                        disabled={!instance.Files?.length}
                    >
                        Store
                    </button>
                    {(storeProgress[instance.SeriesInstanceUID] > 0) && (
                        <div className="w-full bg-gray-200 rounded-full h-1 h-1 -mb-1">
                            <div
                                className="bg-blue-500 h-1 rounded-full"
                                style={{
                                    width: `${storeProgress[instance.SeriesInstanceUID]}%`,
                                }}
                            ></div>
                        </div>
                    )}
                </div>
            )}
            <button
                className="text-blue-500 hover:underline"
                onClick={async (e) => {
                    e.stopPropagation();
                    setShowFilePreviewInstance(instance)
                    renderInstance(instance);
                }}
            >
                View
            </button>
        </div>
    );

    const columns = [
        { key: 'PatientID', label: 'Patient ID', hideable: false, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/24' },
        { key: 'PatientName', label: 'Patient Name', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/12' },
        { key: 'StudyDescription', label: 'Study Description', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/12' },
        { key: 'StudyInstanceUID', label: 'Study Instance UID', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/12' },
        { key: 'ReferencedSeriesInstanceUID', label: 'Referenced Series Instance UID', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/12' },
        { key: 'Modality', label: 'Modality', hideable: true, sortable: true, defaultVisible: true, filterable: true, width: 'w-1/24' },
        { key: 'SeriesInstanceUID', label: 'Series Instance UID', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/12' },
        { key: 'NumberOfFiles', label: 'Number of Files', hideable: true, sortable: true, defaultVisible: false, filterable: false, width: 'w-1/24', repr: (_, instance) => instance.Files?.length || 0, display: (_, instance) => instance.Files?.length || '-' },
        { key: 'size', label: 'Total Size', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/24', display: value => `${(value / (1024 * 1024)).toFixed(2)} MB` },
        { key: 'local', label: 'Available Locally', hideable: true, sortable: true, defaultVisible: false, filterable: true, width: 'w-1/24', display: (value => value ? 'Yes' : 'No') },
        { key: 'importDate', label: 'Import Date', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/24', display: DateDisplay},
        { key: 'tasks', label: 'Tasks', hideable: true, sortable: true, defaultVisible: true, filterable: false, width: 'w-1/24', repr: (_, instance) => instance.tasks?.length || 0, display: TaskLink},
        { key: 'Actions', label: 'Actions', hideable: false, sortable: false, defaultVisible: true, filterable: false, width: 'w-1/12', display: InstanceActions }
    ];

    return (
        <div className="flex flex-col items-center justify-center flex-grow gap-10">
            {/* <PageHeader
                title="DICOM Browser"
                buttons={
                    <>
                        <PageHeaderCancellableProgress
                            label="Run model"
                            icon={<PlayIcon className="h-5 w-5" />}
                            progress={null}
                            onCancel={() => alert('Cancel processing not implemented yet')}
                        />
                        <PageHeaderButton
                            onClick={() => setShowProcessingConfirmationDialog(true)}
                            icon={<PlayIcon className="h-5 w-5" />}
                            disabled={selectedInstances.length === 0}
                            text="Confirm Processing"
                        />
                    </>
                }
            /> */}

            { showProcessingConfirmationDialog ?
                <ProcessingConfitmation 
                    instances={selectedInstances} 
                    model={model} 
                    onClose={() => setShowProcessingConfirmationDialog(false)} 
                />
             :
                <Table 
                    name="dicomstore"
                    columns={columns}
                    rows={Object.values(instances).filter((i) => !showOnlyHighlighted || isInstanceHighlighted(i))}
                    onDrop={(items) => processDataTransferItemList(items, (n, m) => {
                        console.log('Progress:', n, m);
                        setUploadProgress((n / m) * 100)
                    }, handleProcessingProgress)}
                    onSelect={(instance) => {
                        setInstances((prev) => {
                            const updatedInstances = { ...prev };
                            updatedInstances[instance.SeriesInstanceUID].selected = !updatedInstances[instance.SeriesInstanceUID].selected;
                            return updatedInstances;
                        });
                    }}
                    options={[
                        {label: "Clear Table", onClick: clearTable},
                        {label: "Save Selection Locally", onClick: saveSelectedInstancesLocally, disabled: selectedInstances.length === 0},
                        {label: "Load Local Files", onClick: loadLocallySavedInstances},
                        {label: <span className="text-red-800">Remove Selected Local Files</span>, onClick: removeSelectedLocallySavedInstances, disabled: selectedInstances.length === 0},
                        {label: <span className="text-red-800">Remove Local Files</span>, onClick: removeAllLocallySavedInstances},
                        {label: "Show Only Highlighted", onClick: () => setShowOnlyHighlighted(!showOnlyHighlighted), checked: showOnlyHighlighted, disabled: !highlightSID },
                    ]}
                    summary={(filteredInstances) => {
                        const totalSize = filteredInstances.reduce((sum, instance) => sum + (instance.size || 0), 0) / (1024 * 1024 * 1024); // Convert to GB
                        return (
                            <div className="flex items-center gap-2 flex-grow px-5">
                                <div className="text-sm text-gray-700">
                                    Total: {filteredInstances.length} instance{filteredInstances.length === 1 ? '' : 's'}
                                </div>
                                <div className="text-sm text-gray-700 min-w-28">
                                    Size: {totalSize.toFixed(2)} GB
                                </div>
                                { uploadProgress > 0 && (
                                    <div className="text-sm text-gray-700 w-40 ml-3">
                                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                                className="bg-blue-500 h-2 rounded-full"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>     
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }}
                    buttons={
                        model ?
                            <TableControlsButton 
                                onClick={() => setShowProcessingConfirmationDialog(true)} 
                                className="bg-green-500 disabled:bg-green-400 disabled:text-gray-800 hover:bg-green-600 hover:shadow-lg disabled:shadow-none text-white transition-all duration-200"
                                disabled={selectedInstances.length === 0}
                            >
                                <span className="flex items-center gap-2 whitespace-nowrap">
                                    <PlayIcon className="h-5 w-5" />
                                    Run {model.label}
                                </span>
                            </TableControlsButton>
                        :
                            <TableControlsButton 
                                onClick={() => router.visit(`/models/`)}
                                className="!bg-blue-400 hover:!bg-blue-500 text-white transition-all duration-200"
                                icon={<PlayIcon className="h-5 w-5" />}
                                text="Confirm Processing"
                            >
                                <span className="flex items-center gap-2 whitespace-nowrap">
                                    <CubeIcon className="h-5 w-5" />
                                    Select Model
                                </span>
                            </TableControlsButton>
                    }
                    side={ showFilePreviewInstance &&
                        <InstancePreview 
                            instance={showFilePreviewInstance} 
                            onClose={() => setShowFilePreviewInstance(null) } 
                        />
                    }
                    highlight={ (instance) => isInstanceHighlighted(instance) }
                    highlightClassName='bg-yellow-50'
                />
            }
        </div>
    );
}

function InstancePreview({ instance, onClose }) {

    const elementRef = useRef(null)

    useEffect(() => {
        setup(elementRef.current)
    }, [elementRef])

    return (
        <div className={clsx('bg-gray-200 h-full rounded-md p-2', !instance && 'hidden')}>

            <div className="flex gap-5 justify-between items-center mt-2 mb-5">
                <div>
                    <span className="p-1 bg-gray-100 text-sm rounded-lg mr-2">
                        {instance.Modality}
                    </span>
                    {instance.PatientID}
                </div>
                <button 
                    className="px-3 py-2 text-sm rounded text-gray-800 bg-gray-300 hover:bg-gray-400 font-semibold disabled:opacity-50 disabled:cursor-default"
                    onClick={onClose}
                >
                    Close
                </button>
            </div> 

            { /* DICOM viewer container */ }
            <div
                ref={elementRef}
                style={{
                    width: "512px",
                    height: "512px",
                    backgroundColor: "#000",
                    borderRadius: '5px'
                }}
            />

            {/* Instance details */}
            <div className="mt-5 text-sm text-gray-700">
                <div><strong>Patient Name:</strong> {instance.PatientName || 'N/A'}</div>
                <div><strong>Patient ID:</strong> {instance.PatientID || 'N/A'}</div>
                <div><strong>Study Description:</strong> {instance.StudyDescription || 'N/A'}</div>
                <div><strong>Modality:</strong> {instance.Modality || 'N/A'}</div>
                <div><strong>Size:</strong> {(instance.size / (1024 * 1024)).toFixed(2)} MB {instance.local && "(Stored Locally)"}</div>
                <div><strong>Import Date:</strong> {instance.importDate ? new Date(instance.importDate).toLocaleString() : 'N/A'}</div>
            </div>

        </div>
    )
}

function PageHeaderButton({ onClick, icon, text, disabled }) {
    return (
        <button
            className={`flex items-center gap-x-1 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={onClick}
            disabled={disabled}
        >
            {icon}
            {text}
        </button>
    );
}

function CancellableProgressIndicator({ icon, label, progress = null, onCancel, cancellable = true }) {
    const _cancellable = cancellable && typeof onCancel === 'function' && progress != null && progress < 100;
    return (
        <div 
            className={clsx(
                'bg-gray-400 p-2 rounded-md shadow-sm overflow-hidden text-sm', 
                _cancellable && 'cursor-pointer hover:bg-red-400 transition-colors duration-200 group'
            )} 
            onClick={() => _cancellable && onCancel()}
        >
            <div className="flex flex-col items-center gap-0">
                {/* title */}
                <p className="flex gap-2 items-center text-white font-semibold">
                    {icon}    
                    {label}
                </p>

                {/* progress bar */}
                <div className={clsx("w-full flex items-center relative transaction-all duration-200", progress == null ? 'h-0' : ' h-1 mt-1')}>
                    <div className="bg-white/30 rounded-sm overflow-hidden group-hover:mr-10 transaction-all duration-200 w-full">
                        <div
                            className="bg-white h-1 rounded-sm"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-white text-sm absolute right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Stop
                    </span>
                </div>
            </div>
        </div>
    );
}

function ProcessingConfitmation({ instances, model, onClose }) {

    const [isAccepted, setAccepted] = useState(false);
    const [isAccepted2, setAccepted2] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const { pgrogress: p, setProgress: sp } = useUploadProgress();

    // computated
    const credits = model.credits * instances.length;
    const isMatching = (instance, model) => {
        const modelModalities = (model.modalities || []).map(m => m.toLowerCase());
        return instance.Modality && modelModalities.includes(instance.Modality.toLowerCase());
    }
    const isAllMatching = instances.every(instance => isMatching(instance, model));
    const canProceed = instances.length > 0 && isAllMatching && isAccepted && isAccepted2 && !isProcessing;

    // callbacks
    const processTasks = () => {
        let uploadProgress = {};
        setIsProcessing(true);
        const promises = instances.map((instance) => 
            Instance.process(instance, model.name, (progress) => {
                console.log(`Upload progress for ${instance.SeriesInstanceUID}: ${progress}%`);
                uploadProgress[instance.SeriesInstanceUID] = progress;
                const totalProgress = Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) / Object.keys(uploadProgress).length;
                setUploadProgress(totalProgress);
                sp({...p, [instance.SeriesInstanceUID]: totalProgress });
            })
            .then(() => {
                console.log('Processing task completed:', instance)

                // Remove the instance from progress tracking after successful processing
                const up = { ...p };
                delete up[instance.SeriesInstanceUID];
                sp(up);
            })
            .catch((error) => console.error('Error processing task:', error))
        );
        Promise.all(promises).finally(() => setIsProcessing(false));
    };

    const cancelTasks = () => {
        
        // TODO: cancel all tasks and report success / failure, include link to task page
        
        setIsProcessing(false);
    }

    // components
    const ModalityTag = ({modalities, color = 'gray'}) => (
        <span className={clsx(
            "text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full",
            `bg-${color}-100 text-${color}-800`
        )}>
            Modality: {String(modalities).toUpperCase()}
        </span>
    )

    // render
    return (
        <div className="px-8 flow-root w-full">

            {/* Header for the confirmation dialog */}
            <div className="flex items-center mb-4 justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Confirm Processing</h2>
                </div>
                <div className="flex gap-4 ml-auto">
                    <TableControlsButton 
                        onClick={onClose} 
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 transition-all duration-200"
                        disabled={isProcessing}
                    >
                        <span className="flex items-center gap-2">
                            Go Back
                        </span>
                    </TableControlsButton>
                    { isProcessing ?
                        <CancellableProgressIndicator 
                            icon={<PlayIcon className="h-5 w-5" />}
                            label="Uploading..."
                            progress={uploadProgress}
                            onCancel={cancelTasks}
                        />
                    :
                        <TableControlsButton 
                            onClick={processTasks} 
                            className="bg-green-500 disabled:bg-green-400 disabled:text-gray-800 hover:bg-green-600 hover:shadow-lg disabled:shadow-none text-white transition-all duration-200"
                            disabled={!canProceed}
                        >
                            <span className="flex items-center gap-2">
                                <PlayIcon className="h-5 w-5" />
                                Start Processing
                            </span>
                        </TableControlsButton>
                    }
                </div>
            </div>

            {/* Model details, title, description, modality */}
            <div className="bg-white rounded-lg border p-6 mb-4">
                <h3 className="text-sd font-semibold mb-4">{model.label}</h3>
                <p className="text-md text-gray-600 mb-2">{model.description}</p>
                <div className="mt-5">
                    <ModalityTag modalities={model.modalities} />
                </div>
            </div>

            {/* List / summary of selected images to be processed */}
            <div className={clsx(
                "bg-white rounded-lg border p-6 mb-4",
                !isAllMatching && 'border-red-300',
            )}>
                <h3 className="text-md font-semibold mb-4">Selected Instances</h3>
                <ul className="list-disc pl-5">
                    {instances.map((instance) => {
                        const isModalityMatching = isMatching(instance, model);
                        return (
                            <li key={instance.SeriesInstanceUID} className="mb-2">
                                <div className="flex items-center justify-between">
                                    <span>{instance.PatientID} ({instance.SeriesInstanceUID})</span>
                                    <span className="text-sm text-gray-500">
                                        <ModalityTag 
                                            modalities={instance.Modality} 
                                            color={isModalityMatching ? 'green' : 'red'} 
                                        />
                                    </span>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>

            {/* Cost overview and confirmation to immediately start processing and waiviing 14 days right of withdrawal */}
            <div className="bg-white rounded-lg border p-6 mb-4">
                {/* <h3 className="text-md font-semibold mb-4">Cost Overview</h3> */}
              
                {/* credits */}
                <p className="text-md text-gray-800 mb-2">
                    <span className="font-semibold">Credits:</span> {credits} {credits === 1 ? 'credit' : 'credits'}
                </p>

                {/* confirmation checkbox */}
                <p className="text-md text-gray-600 mb-2">
                    <input 
                        type="checkbox" 
                        id="acceptProcessing2" 
                        checked={isAccepted2} 
                        disabled={isProcessing}
                        onChange={(e) => setAccepted2(e.target.checked)} 
                        className="mr-2"
                    />
                    <label htmlFor="acceptProcessing2">
                        I hereby confitrm that I have anonymized all images. I understand that the selected images will be uploaded to a cloud server in Germany for processing and deleted after the processing is done.
                    </label>
                </p>

                 <p className="text-md text-gray-600 mb-2">
                    <input 
                        type="checkbox" 
                        id="acceptProcessing" 
                        checked={isAccepted} 
                        disabled={isProcessing}
                        onChange={(e) => setAccepted(e.target.checked)} 
                        className="mr-2"
                    />
                    <label htmlFor="acceptProcessing">
                       I hereby agree to start with the processing <strong>before the end of the 14-day withdrawal period</strong> and I understand that I will lose my right of withdrawal with complete fullfillment of the contract. I confirm I have been informed of, and agree to, the Terms and Conditions, including the information on the right of withdrawal.
                    </label>
                </p>
            </div>
        </div>
    );
}